/**
 * useRAG Hook
 * Custom hook for managing RAG (Retrieval-Augmented Generation) functionality
 */

import { useState, useCallback, useEffect } from 'react';
import { ragService, ContractEnhancementResponse, LegalQueryResponse } from '../services/ragService';

export interface UseRAGResult {
  // Status
  isAvailable: boolean;
  isLoading: boolean;
  error: string | null;

  // Contract Enhancement
  recommendations: string[];
  legalEnhancements: ContractEnhancementResponse['legal_enhancements'];
  enhanceContract: (contractType: 'purchase' | 'rental' | 'employment', contractData: Record<string, any>) => Promise<void>;
  clearRecommendations: () => void;

  // Legal Queries
  queryResponse: string | null;
  queryMetadata: LegalQueryResponse['metadata'] | null;
  queryLegalKnowledge: (query: string, mode?: 'naive' | 'local' | 'global' | 'hybrid') => Promise<void>;
  clearQuery: () => void;

  // Status
  checkAvailability: () => Promise<void>;
}

export const useRAG = (): UseRAGResult => {
  // Status states
  const [isAvailable, setIsAvailable] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Contract enhancement states
  const [recommendations, setRecommendations] = useState<string[]>([]);
  const [legalEnhancements, setLegalEnhancements] = useState<ContractEnhancementResponse['legal_enhancements']>([]);

  // Query states
  const [queryResponse, setQueryResponse] = useState<string | null>(null);
  const [queryMetadata, setQueryMetadata] = useState<LegalQueryResponse['metadata'] | null>(null);

  /**
   * Check if RAG service is available
   */
  const checkAvailability = useCallback(async () => {
    try {
      const available = await ragService.isAvailable();
      setIsAvailable(available);
      setError(null);
    } catch (err) {
      setIsAvailable(false);
      setError('RAG služba není dostupná');
      console.warn('RAG service availability check failed:', err);
    }
  }, []);

  /**
   * Enhance contract with legal recommendations
   */
  const enhanceContract = useCallback(async (
    contractType: 'purchase' | 'rental' | 'employment',
    contractData: Record<string, any>
  ) => {
    if (!isAvailable) {
      // Fallback to static recommendations if RAG not available
      const staticRecommendations = ragService.getStaticRecommendations(contractType, contractData);
      setRecommendations(staticRecommendations);
      setLegalEnhancements([]);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await ragService.enhanceContract({
        contract_type: contractType,
        contract_data: contractData
      });

      if (response.success) {
        setRecommendations(response.recommendations || []);
        setLegalEnhancements(response.legal_enhancements || []);
      } else {
        throw new Error(response.error || 'Vylepšení smlouvy selhalo');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Nastala chyba při analýze smlouvy';
      setError(errorMessage);
      
      // Fallback to static recommendations
      const staticRecommendations = ragService.getStaticRecommendations(contractType, contractData);
      setRecommendations(staticRecommendations);
      setLegalEnhancements([]);
      
      console.error('Contract enhancement failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAvailable]);

  /**
   * Query legal knowledge base
   */
  const queryLegalKnowledge = useCallback(async (
    query: string,
    mode: 'naive' | 'local' | 'global' | 'hybrid' = 'hybrid'
  ) => {
    if (!isAvailable) {
      setError('RAG služba není dostupná pro dotazy');
      return;
    }

    setIsLoading(true);
    setError(null);
    setQueryResponse(null);
    setQueryMetadata(null);

    try {
      const response = await ragService.queryLegalKnowledge({
        query,
        mode,
        max_results: 5
      });

      if (response.success) {
        setQueryResponse(response.response);
        setQueryMetadata(response.metadata);
      } else {
        throw new Error(response.error || 'Dotaz na právní znalosti selhal');
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Nastala chyba při zpracování dotazu';
      setError(errorMessage);
      console.error('Legal knowledge query failed:', err);
    } finally {
      setIsLoading(false);
    }
  }, [isAvailable]);

  /**
   * Clear contract recommendations
   */
  const clearRecommendations = useCallback(() => {
    setRecommendations([]);
    setLegalEnhancements([]);
    setError(null);
  }, []);

  /**
   * Clear query results
   */
  const clearQuery = useCallback(() => {
    setQueryResponse(null);
    setQueryMetadata(null);
    setError(null);
  }, []);

  // Check availability on mount
  useEffect(() => {
    checkAvailability();
  }, [checkAvailability]);

  return {
    // Status
    isAvailable,
    isLoading,
    error,

    // Contract Enhancement
    recommendations,
    legalEnhancements,
    enhanceContract,
    clearRecommendations,

    // Legal Queries
    queryResponse,
    queryMetadata,
    queryLegalKnowledge,
    clearQuery,

    // Utilities
    checkAvailability,
  };
};

export default useRAG;